import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FaComments, FaInfoCircle } from 'react-icons/fa';
import { MdOpenInNew } from 'react-icons/md';
import { formatBookingDate, formatNumber } from 'helpers/utils';
import { TRAVEL_ARRANGEMENT_BY_HR_ADMIN, TRAVEL_TYPE_OF_TRAVEL } from 'app_config';
import { Card, Badge } from 'components/common';
import PersonHoverCard from 'components/common/PersonHoverCard';
import BookingDetailTripForProject from './BookingDetailTripForProject';
import ExpenseDetail from './ExpenseDetail';
import BookingMultipleStopTrip from './BookingMultipleStopTrip';

const BADGE_MAP = { primary: 'primary', secondary: 'default', success: 'success', warning: 'warning', danger: 'danger', info: 'info' };

const Field = ({ label, children }) => (
  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-0.5 sm:tw-gap-2 tw-mb-1.5 tw-text-sm">
    <span className="sm:tw-w-40 sm:tw-flex-shrink-0 tw-font-semibold tw-text-slate-600">{label}</span>
    <span className="tw-text-blue-700 tw-min-w-0 tw-break-words">{children}</span>
  </div>
);
Field.propTypes = { label: PropTypes.node, children: PropTypes.node };

const SectionTitle = ({ children }) => (
  <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">{children}</h6>
);
SectionTitle.propTypes = { children: PropTypes.node };

const BookingDetailInfo = ({ booking }) => {
  const { status } = booking;
  // Only admins get the shortcut into HR records: a traveller's employee page
  // carries personal data (contract, site, manager chain) that everyone else
  // approving a trip has no business opening. profile.is_admin is served by
  // ProfileSerializer.
  const isAdmin = useSelector((s) => s.profile_user?.data?.is_admin) || false;
  const ccRecipients = booking?.cc_recipients || [];

  return (
    <>
      <Card
        padding="none"
        className="tw-mb-3"
        header={(
          <div className="tw-flex tw-items-center tw-justify-between">
            <h5 className="tw-mb-0 tw-font-semibold tw-text-slate-800 tw-flex tw-items-center tw-gap-2"><FaInfoCircle /> Booking information</h5>
            <span className="tw-text-sm">ID#: <strong className="tw-text-blue-700">{booking?.booking_number}</strong></span>
          </div>
        )}
      >
        <div className="tw-bg-slate-50 tw-p-4">
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6 tw-mb-4">
            <div>
              <SectionTitle>Information</SectionTitle>
              <Field label="Trip name">{booking.trip_name}</Field>
              <Field label="Date of travel">{formatBookingDate(booking.date_of_travel)}</Field>
              <Field label="Date of return">{formatBookingDate(booking.date_of_return)}</Field>
              <Field label="Employees traveler">
                <span className="tw-flex tw-flex-wrap tw-gap-1">
                  {(booking.employees || []).map((item) => {
                    // Everyone reviewing a trip gets the hover card — full name,
                    // email, title, site, department — which is what identifying a
                    // traveller actually needs. Only admins get the click-through
                    // into the HR record itself.
                    const badge = (
                      <Badge
                        variant="primary"
                        size="sm"
                        className={isAdmin && item.employee_id ? 'hover:tw-brightness-95' : ''}
                      >
                        {item.employee_name} ({item.title})
                        {isAdmin && item.employee_id && <MdOpenInNew size={10} className="tw-opacity-70" />}
                      </Badge>
                    );

                    return (
                      <PersonHoverCard key={item.id} person={item}>
                        {isAdmin && item.employee_id ? (
                          <a
                            href={`/organization/employees/${item.employee_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tw-no-underline"
                          >
                            {badge}
                          </a>
                        ) : badge}
                      </PersonHoverCard>
                    );
                  })}
                </span>
              </Field>
              <Field label="Number of employees traveler">{(booking.employees && booking.employees.length) || 0}</Field>
              <Field label="Number of days estimated">{booking.number_of_days_estimate}</Field>
            </div>
            <div>
              <SectionTitle>Destination</SectionTitle>
              <Field label="From site office">{booking.site_name}</Field>
              <Field label="Destination">{booking.destination}</Field>
              <Field label="Trip to country">{booking.country_name}</Field>
              <Field label="Type of trip">
                <span className="tw-flex tw-gap-4">
                  {TRAVEL_TYPE_OF_TRAVEL.map((item) => (
                    <label key={item} className="tw-inline-flex tw-items-center tw-gap-1.5">
                      <input type="radio" readOnly checked={item === booking.type_of_travel} />
                      <span>{item}</span>
                    </label>
                  ))}
                </span>
              </Field>
              <Field label="Currency of trip">{booking.currency}</Field>
              {booking.currency === 'USD' && (
                <Field label="Exchange rate">1USD = {formatNumber(booking.exchange_rate)} VND</Field>
              )}
              <Field label="Status"><Badge variant={BADGE_MAP[status?.css_class] || 'default'} size="sm">{status?.name}</Badge></Field>
            </div>
          </div>

          <div className="tw-mb-4">
            <SectionTitle>Arrangement by HR Admin</SectionTitle>
            <div className="tw-border tw-border-solid tw-border-slate-200 tw-rounded tw-p-3 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-2">
              {TRAVEL_ARRANGEMENT_BY_HR_ADMIN.map((option) => (
                <label key={option} className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm">
                  <input type="checkbox" readOnly checked={booking.arrangement_by_hr_admin && booking.arrangement_by_hr_admin.includes(option)} />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
            <div>
              <Field label="Purpose of trip">{booking.purpose_of_travel}</Field>
              <Field label="Description"><span style={{ whiteSpace: 'pre-line' }}>{booking.description_purpose_of_travel}</span></Field>
            </div>
            <div>
              <Field label="CC emails">
                <span>
                  {/* CC entries are stored as user names ("LDQ"), which say
                      nothing on their own — the same hover card as the traveller
                      list resolves each to a person. cc_recipients comes from the
                      detail serializer; fall back to the raw names when an older
                      payload has none. */}
                  {ccRecipients.length > 0 ? (
                    <span className="tw-inline-flex tw-flex-wrap tw-gap-1.5">
                      {ccRecipients.map((person, idx) => (
                        <PersonHoverCard key={`${person.name}-${idx}`} person={person}>
                          <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-border tw-border-solid ${
                            person.email
                              ? 'tw-bg-blue-50 tw-text-blue-700 tw-border-blue-100'
                              : 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200'
                          }`}>
                            {person.name}
                          </span>
                        </PersonHoverCard>
                      ))}
                    </span>
                  ) : booking?.cc_emails_optional?.join(', ')}
                  <span className="tw-block tw-text-xs tw-text-slate-500 tw-mt-1"><FaInfoCircle className="tw-inline tw-mr-1" /> CC others for email tracking of the trip upon approval.</span>
                </span>
              </Field>
              <SectionTitle>Created</SectionTitle>
              <Field label="Submited by">{booking.submited_by}</Field>
              <Field label="Submited date">{booking.submited_date}</Field>
              <Field label="Updated date">{booking.updated_date}</Field>
            </div>
          </div>
        </div>
      </Card>

      <BookingMultipleStopTrip booking={booking} />
      <BookingDetailTripForProject booking={booking} />
      <ExpenseDetail booking={booking} />

      <Card
        padding="none"
        className="tw-mb-3"
        header={<h5 className="tw-mb-0 tw-font-semibold tw-text-slate-800 tw-flex tw-items-center tw-gap-2"><FaComments /> Notes</h5>}
      >
        <div className="tw-bg-slate-50 tw-p-4">
          <Field label="Comment for booking"><span style={{ whiteSpace: 'pre-line' }}>{booking.comment_for_booking}</span></Field>
        </div>
      </Card>
    </>
  );
};

BookingDetailInfo.propTypes = {
  booking: PropTypes.object.isRequired,
};

export default BookingDetailInfo;
